import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  FiBox,
  FiCheckCircle,
  FiChevronDown,
  FiClock,
  FiList,
  FiMap,
  FiMapPin,
  FiNavigation,
  FiRefreshCw,
  FiSearch,
  FiTrash2,
} from "react-icons/fi";

import { trackingApi } from "../services/trackingApi";
import { hasPermission } from "../utils/permissions";
import AllShipmentsMap from "../components/tracking/AllShipmentsMap";
import ShipsGoLiveMap from "../components/tracking/ShipsGoLiveMap";
import {
  buildTrackingSummary,
  getDestinationPort,
  getEta,
  getLastEvent,
  getOriginPort,
  getRouteProgress,
  getTrackingNo,
  getTrackingStatus,
  getVesselName,
  getVoyageNo,
} from "../utils/shipsGoTracking";

const AUTO_REFRESH_MS = 5 * 60 * 1000;

const extractRows = (response) => {
  const data = response?.data;

  if (Array.isArray(data?.shipments)) return data.shipments;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data)) return data;

  return [];
};

const formatDate = (value, withTime = false) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  });
};

const getSearchText = (shipment) => {
  const status = getTrackingStatus(shipment);
  const lastEvent = getLastEvent(shipment);

  return [
    shipment?.requestId,
    shipment?.containerNumber,
    shipment?.blNumber,
    shipment?.bookingNumber,
    shipment?.shippingLine,
    shipment?.referenceNo,
    shipment?.tags,
    status.label,
    lastEvent.name,
    getVesselName(shipment),
    getVoyageNo(shipment),
    getOriginPort(shipment),
    getDestinationPort(shipment),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
};

const statusBadgeClass = (statusGroup) => {
  const base =
    "inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold";

  if (statusGroup === "delivered") {
    return `${base} border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/70 dark:bg-emerald-950/40 dark:text-emerald-300`;
  }

  if (statusGroup === "arrived") {
    return `${base} border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-900/70 dark:bg-violet-950/40 dark:text-violet-300`;
  }

  return `${base} border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/70 dark:bg-blue-950/40 dark:text-blue-300`;
};

const filterOptions = [
  { value: "all", label: "All Shipments" },
  { value: "inTransit", label: "In Transit" },
  { value: "arrived", label: "Arrived at Port" },
  { value: "delivered", label: "Delivered" },
];

function ConfirmDeleteDialog({
  open,
  shipment,
  loading,
  onCancel,
  onConfirm,
}) {
  if (!open || !shipment) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-6 shadow-2xl dark:border-slate-800 dark:bg-slate-900">
        <div className="grid h-14 w-14 place-items-center rounded-2xl bg-red-50 text-xl text-red-600 dark:bg-red-950/50 dark:text-red-300">
          <FiTrash2 />
        </div>

        <h3 className="mt-5 text-xl font-bold text-slate-950 dark:text-white">
          Delete tracking shipment?
        </h3>

        <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">
          This will remove{" "}
          <strong className="text-slate-800 dark:text-slate-200">
            {getTrackingNo(shipment)}
          </strong>{" "}
          from this portal. The linked container and the remote ShipsGo
          shipment will not be deleted.
        </p>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            disabled={loading}
            onClick={onCancel}
            className="rounded-xl border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            Cancel
          </button>

          <button
            type="button"
            disabled={loading}
            onClick={onConfirm}
            className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-red-700 disabled:opacity-60"
          >
            <FiTrash2 />
            {loading ? "Deleting..." : "Delete Tracking"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ShipmentListItem({ shipment, selected, onSelect }) {
  const status = getTrackingStatus(shipment);
  const lastEvent = getLastEvent(shipment);

  return (
    <button
      type="button"
      onClick={() => onSelect(shipment)}
      className={`w-full rounded-2xl border p-4 text-left transition duration-200 ${
        selected
          ? "border-blue-400 bg-blue-50 shadow-sm dark:border-blue-500 dark:bg-blue-950/35"
          : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:hover:border-slate-700 dark:hover:bg-slate-800/70"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate font-bold text-slate-900 dark:text-slate-100">
            {getTrackingNo(shipment)}
          </p>
          <p className="mt-1 truncate text-xs text-slate-500 dark:text-slate-400">
            {shipment.shippingLine || "Carrier not available"}
            {getVesselName(shipment) !== "-"
              ? ` • ${getVesselName(shipment)}`
              : ""}
          </p>
        </div>

        <span className={statusBadgeClass(status.group)}>{status.label}</span>
      </div>

      <p className="mt-3 truncate text-xs text-slate-600 dark:text-slate-300">
        {getOriginPort(shipment)} → {getDestinationPort(shipment)}
      </p>

      <div className="mt-3 flex items-center justify-between gap-3 text-[11px] text-slate-400 dark:text-slate-500">
        <span className="truncate">{lastEvent.name || "No event available"}</span>
        <span className="shrink-0">
          {formatDate(shipment.lastFetchedAt || shipment.updatedAt, true)}
        </span>
      </div>
    </button>
  );
}

function ShipmentDetails({ shipment }) {
  if (!shipment) {
    return (
      <div className="grid min-h-[330px] place-items-center rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center dark:border-slate-700 dark:bg-slate-950">
        <div>
          <FiBox className="mx-auto text-3xl text-slate-400" />
          <p className="mt-3 font-semibold text-slate-900 dark:text-slate-100">
            Select a shipment
          </p>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Choose a shipment from the list or map to view its details.
          </p>
        </div>
      </div>
    );
  }

  const status = getTrackingStatus(shipment);
  const lastEvent = getLastEvent(shipment);
  const progress = getRouteProgress(shipment);

  const details = [
    { label: "Tracking No", value: getTrackingNo(shipment) },
    { label: "Carrier", value: shipment.shippingLine || "-" },
    { label: "Vessel", value: getVesselName(shipment) },
    { label: "Voyage", value: getVoyageNo(shipment) },
    { label: "Origin", value: getOriginPort(shipment) },
    { label: "Destination", value: getDestinationPort(shipment) },
    { label: "ETA", value: formatDate(getEta(shipment)) },
    {
      label: "Last Updated",
      value: formatDate(shipment.lastFetchedAt || shipment.updatedAt, true),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
              Current shipment
            </p>
            <p className="mt-1 font-bold text-slate-900 dark:text-slate-100">
              {getTrackingNo(shipment)}
            </p>
          </div>
          <span className={statusBadgeClass(status.group)}>{status.label}</span>
        </div>

        <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
          <div
            className={`h-full rounded-full transition-all duration-500 ${
              status.group === "delivered"
                ? "bg-emerald-500"
                : status.group === "arrived"
                  ? "bg-violet-500"
                  : "bg-blue-500"
            }`}
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className="mt-2 flex justify-between text-xs text-slate-500 dark:text-slate-400">
          <span>Journey progress</span>
          <span className="font-semibold">{progress}%</span>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {details.map((item) => (
          <div
            key={item.label}
            className="rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-950"
          >
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              {item.label}
            </p>
            <p className="mt-1 break-words text-sm font-semibold text-slate-900 dark:text-slate-100">
              {item.value}
            </p>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
          Latest event
        </p>
        <p className="mt-2 font-semibold text-slate-900 dark:text-slate-100">
          {lastEvent.name || "No event available"}
        </p>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          {formatDate(lastEvent.date, true)}
        </p>
      </div>
    </div>
  );
}

export default function Tracking() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [shipments, setShipments] = useState([]);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [leftTab, setLeftTab] = useState("list");
  const [mapMode, setMapMode] = useState("all");
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [lastLoadedAt, setLastLoadedAt] = useState(null);

  const canUpdateTracking = hasPermission("tracking", "update");
  const canDeleteTracking = hasPermission("tracking", "delete");

  const requestRunningRef = useRef(false);

  const loadShipments = useCallback(
    async ({ silent = false } = {}) => {
      if (requestRunningRef.current) return;

      requestRunningRef.current = true;
      if (!silent) setLoading(true);
      setError("");

      try {
        const response = await trackingApi.list();
        const rows = extractRows(response);
        const requestedId = searchParams.get("shipment");

        setShipments(rows);
        setSelectedShipment((previous) => {
          const fromUrl = rows.find((item) => String(item.id) === requestedId);
          const existing = rows.find((item) => item.id === previous?.id);
          return fromUrl || existing || rows[0] || null;
        });
        setLastLoadedAt(new Date());
      } catch (loadError) {
        console.error("TRACKING PAGE LOAD ERROR:", loadError);
        setError(
          loadError.response?.data?.message || "Failed to load tracking data.",
        );
      } finally {
        requestRunningRef.current = false;
        if (!silent) setLoading(false);
      }
    },
    [searchParams],
  );

  /*
   * Do not load tracking data immediately when the screen opens.
   * The local tracking list refreshes only every five minutes.
   * Manual reload and ShipsGo refresh buttons continue to work.
   */
  useEffect(() => {
    const intervalId = window.setInterval(() => {
      loadShipments({ silent: true });
    }, AUTO_REFRESH_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [loadShipments]);

  const summary = useMemo(() => buildTrackingSummary(shipments), [shipments]);

  const filteredShipments = useMemo(() => {
    const query = search.trim().toLowerCase();

    return shipments.filter((shipment) => {
      const status = getTrackingStatus(shipment);
      const statusMatches =
        statusFilter === "all" || status.group === statusFilter;
      const searchMatches = !query || getSearchText(shipment).includes(query);

      return statusMatches && searchMatches;
    });
  }, [search, shipments, statusFilter]);

  useEffect(() => {
    if (!filteredShipments.length) return;

    const selectedStillVisible = filteredShipments.some(
      (shipment) => shipment.id === selectedShipment?.id,
    );

    if (!selectedStillVisible) {
      setSelectedShipment(filteredShipments[0]);
    }
  }, [filteredShipments, selectedShipment?.id]);

  const handleSelectShipment = (shipment) => {
    setSelectedShipment(shipment);
    setLeftTab("details");

    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("shipment", shipment.id);
    setSearchParams(nextParams, { replace: true });
  };

  const refreshSelectedShipment = async () => {
    if (!selectedShipment?.id) return;

    setRefreshing(true);
    setError("");

    try {
      const response = await trackingApi.refresh(selectedShipment.id);
      const updated = response.data?.shipment || response.data;

      setShipments((previous) =>
        previous.map((item) => (item.id === updated.id ? updated : item)),
      );
      setSelectedShipment(updated);
      setLastLoadedAt(new Date());
    } catch (refreshError) {
      console.error("TRACKING REFRESH ERROR:", refreshError);
      setError(
        refreshError.response?.data?.message ||
          "Failed to refresh the selected shipment.",
      );
    } finally {
      setRefreshing(false);
    }
  };

  const refreshAllShipments = async () => {
    if (!canUpdateTracking || refreshingAll) return;

    setRefreshingAll(true);
    setError("");
    setNotice("");

    try {
      const response = await trackingApi.refreshAll();
      const successCount = Number(response.data?.successCount || 0);
      const failedCount = Number(response.data?.failedCount || 0);

      await loadShipments({ silent: true });

      setNotice(
        failedCount > 0
          ? `${successCount} shipment(s) refreshed; ${failedCount} failed.`
          : `${successCount} shipment(s) refreshed from ShipsGo.`,
      );
    } catch (refreshError) {
      console.error("REFRESH ALL TRACKING ERROR:", refreshError);
      setError(
        refreshError.response?.data?.message ||
          "Failed to refresh tracking records from ShipsGo.",
      );
    } finally {
      setRefreshingAll(false);
    }
  };

  const requestDeleteShipment = () => {
    if (!selectedShipment || !canDeleteTracking) return;
    setDeleteConfirm(selectedShipment);
  };

  const deleteSelectedShipment = async () => {
    if (!deleteConfirm?.id || deleting) return;

    setDeleting(true);
    setError("");
    setNotice("");

    try {
      await trackingApi.remove(deleteConfirm.id);

      const remaining = shipments.filter(
        (shipment) => shipment.id !== deleteConfirm.id,
      );
      const nextSelected = remaining[0] || null;

      setShipments(remaining);
      setSelectedShipment(nextSelected);
      setDeleteConfirm(null);
      setLeftTab(nextSelected ? "details" : "list");
      setMapMode(nextSelected ? mapMode : "all");
      setLastLoadedAt(new Date());
      setNotice("Tracking shipment deleted successfully.");

      const nextParams = new URLSearchParams(searchParams);

      if (nextSelected?.id) {
        nextParams.set("shipment", nextSelected.id);
      } else {
        nextParams.delete("shipment");
      }

      setSearchParams(nextParams, { replace: true });
    } catch (deleteError) {
      console.error("DELETE TRACKING ERROR:", deleteError);
      setError(
        deleteError.response?.data?.message ||
          "Failed to delete the tracking shipment.",
      );
    } finally {
      setDeleting(false);
    }
  };

  const summaryCards = [
    {
      label: "All",
      value: summary.total || 0,
      icon: FiBox,
      className: "text-blue-600 dark:text-blue-400",
    },
    {
      label: "In Transit",
      value: summary.inTransit || 0,
      icon: FiNavigation,
      className: "text-cyan-600 dark:text-cyan-400",
    },
    {
      label: "Arrived",
      value: summary.arrived || 0,
      icon: FiMapPin,
      className: "text-violet-600 dark:text-violet-400",
    },
    {
      label: "Delivered",
      value: summary.delivered || 0,
      icon: FiCheckCircle,
      className: "text-emerald-600 dark:text-emerald-400",
    },
  ];

  return (
    <div className="p-6 text-slate-900 dark:text-slate-100">
      <ConfirmDeleteDialog
        open={Boolean(deleteConfirm)}
        shipment={deleteConfirm}
        loading={deleting}
        onCancel={() => {
          if (!deleting) setDeleteConfirm(null);
        }}
        onConfirm={deleteSelectedShipment}
      />
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-950 dark:text-white">
            Live Shipment Map
          </h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            View all ShipsGo container shipments, current positions, routes, and
            statuses in one place.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("/track-shipment")}
            className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 dark:bg-blue-600 dark:hover:bg-blue-700"
          >
            <FiSearch />
            Track Shipment
          </button>

          {canUpdateTracking ? (
            <button
              type="button"
              onClick={refreshAllShipments}
              disabled={refreshingAll}
              className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:opacity-60"
            >
              <FiRefreshCw
                className={refreshingAll ? "animate-spin" : ""}
              />
              {refreshingAll ? "Refreshing ShipsGo..." : "Refresh All ShipsGo"}
            </button>
          ) : null}

          <button
            type="button"
            onClick={() => loadShipments()}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FiRefreshCw className={loading ? "animate-spin" : ""} />
            Reload Local Data
          </button>
        </div>
      </div>

      {error ? (
        <div className="mt-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-300">
          {error}
        </div>
      ) : null}

      {notice ? (
        <div className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-300">
          {notice}
        </div>
      ) : null}

      <div className="mt-6 grid gap-6 xl:grid-cols-[360px_minmax(0,1fr)]">
        <aside className="space-y-5">
          <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-bold text-slate-900 dark:text-slate-100">
                  Filters
                </h2>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Ocean container shipments only
                </p>
              </div>
              <FiChevronDown className="text-slate-400" />
            </div>

            <div className="relative mt-4">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search container, BL, vessel..."
                className="w-full rounded-xl border border-slate-200 bg-white py-3 pl-10 pr-4 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:placeholder:text-slate-500"
              />
            </div>

            <div className="mt-5 border-t border-slate-100 pt-5 dark:border-slate-800">
              <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                Status
              </p>

              <div className="mt-3 flex flex-wrap gap-2">
                {filterOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setStatusFilter(option.value)}
                    className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                      statusFilter === option.value
                        ? "border-blue-600 bg-blue-600 text-white"
                        : "border-slate-200 bg-white text-slate-600 hover:border-blue-300 hover:text-blue-600 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300 dark:hover:border-blue-500 dark:hover:text-blue-300"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-3 border-t border-slate-100 pt-5 dark:border-slate-800">
              {summaryCards.map(({ label, value, icon: Icon, className }) => (
                <div
                  key={label}
                  className="rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-950"
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {label}
                    </p>
                    <Icon className={className} />
                  </div>
                  <p className="mt-1 text-xl font-bold text-slate-900 dark:text-slate-100">
                    {value}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <div className="grid grid-cols-2 bg-slate-100 p-1 dark:bg-slate-800">
              <button
                type="button"
                onClick={() => setLeftTab("list")}
                className={`inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold transition ${
                  leftTab === "list"
                    ? "bg-white text-slate-900 shadow-sm dark:bg-slate-900 dark:text-white"
                    : "text-slate-500 dark:text-slate-400"
                }`}
              >
                <FiList /> Shipment List
              </button>

              <button
                type="button"
                onClick={() => setLeftTab("details")}
                className={`inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold transition ${
                  leftTab === "details"
                    ? "bg-white text-slate-900 shadow-sm dark:bg-slate-900 dark:text-white"
                    : "text-slate-500 dark:text-slate-400"
                }`}
              >
                <FiBox /> Details
              </button>
            </div>

            <div className="max-h-[610px] overflow-y-auto p-4">
              {leftTab === "list" ? (
                loading ? (
                  <div className="grid min-h-[330px] place-items-center text-sm text-slate-500 dark:text-slate-400">
                    <div className="text-center">
                      <FiRefreshCw className="mx-auto animate-spin text-2xl" />
                      <p className="mt-3">Loading shipments...</p>
                    </div>
                  </div>
                ) : filteredShipments.length ? (
                  <div className="space-y-3">
                    {filteredShipments.map((shipment) => (
                      <ShipmentListItem
                        key={shipment.id}
                        shipment={shipment}
                        selected={shipment.id === selectedShipment?.id}
                        onSelect={handleSelectShipment}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="grid min-h-[330px] place-items-center text-center text-sm text-slate-500 dark:text-slate-400">
                    <div>
                      <FiSearch className="mx-auto text-3xl text-slate-400" />
                      <p className="mt-3 font-semibold text-slate-800 dark:text-slate-200">
                        No shipments found
                      </p>
                      <p className="mt-1">Try another search or status filter.</p>
                    </div>
                  </div>
                )
              ) : (
                <ShipmentDetails shipment={selectedShipment} />
              )}
            </div>
          </section>
        </aside>

        <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="flex flex-col gap-3 border-b border-slate-100 p-4 dark:border-slate-800 lg:flex-row lg:items-center lg:justify-between">
            <div className="inline-flex w-fit rounded-xl bg-slate-100 p-1 dark:bg-slate-800">
              <button
                type="button"
                onClick={() => setMapMode("all")}
                className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition ${
                  mapMode === "all"
                    ? "bg-white text-slate-900 shadow-sm dark:bg-slate-900 dark:text-white"
                    : "text-slate-500 dark:text-slate-400"
                }`}
              >
                <FiMap /> All Shipments
              </button>

              <button
                type="button"
                onClick={() => setMapMode("selected")}
                disabled={!selectedShipment}
                className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 ${
                  mapMode === "selected"
                    ? "bg-white text-slate-900 shadow-sm dark:bg-slate-900 dark:text-white"
                    : "text-slate-500 dark:text-slate-400"
                }`}
              >
                <FiNavigation /> Selected Route
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 dark:border-slate-700 dark:text-slate-300">
                {filteredShipments.length} shipment
                {filteredShipments.length === 1 ? "" : "s"}
              </span>

              {selectedShipment && canUpdateTracking ? (
                <button
                  type="button"
                  onClick={refreshSelectedShipment}
                  disabled={refreshing}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                >
                  <FiRefreshCw className={refreshing ? "animate-spin" : ""} />
                  Refresh ShipsGo
                </button>
              ) : null}

              {selectedShipment && canDeleteTracking ? (
                <button
                  type="button"
                  onClick={requestDeleteShipment}
                  disabled={deleting}
                  className="inline-flex items-center gap-2 rounded-xl border border-red-200 px-3 py-2 text-xs font-semibold text-red-600 transition hover:bg-red-50 disabled:opacity-60 dark:border-red-900/70 dark:text-red-300 dark:hover:bg-red-950/40"
                >
                  <FiTrash2 />
                  Delete
                </button>
              ) : null}
            </div>
          </div>

          <div className="p-4">
            {mapMode === "all" ? (
              <AllShipmentsMap
                shipments={filteredShipments}
                selectedShipment={selectedShipment}
                onSelect={handleSelectShipment}
                height={610}
              />
            ) : selectedShipment ? (
              <ShipsGoLiveMap shipment={selectedShipment} height={610} />
            ) : (
              <div className="grid h-[610px] place-items-center rounded-2xl border border-dashed border-slate-300 bg-slate-50 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-400">
                Select a shipment to view its route.
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2 border-t border-slate-100 px-5 py-4 text-xs text-slate-500 dark:border-slate-800 dark:text-slate-400 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2">
              <FiClock />
              {lastLoadedAt
                ? `Local data checked at ${lastLoadedAt.toLocaleTimeString("en-GB", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}`
                : "Waiting for tracking data"}{" "}
              <span className="text-slate-400">
                • ShipsGo updates run in the backend
              </span>
            </div>

            {selectedShipment ? (
              <p className="truncate">
                Selected: <strong>{getTrackingNo(selectedShipment)}</strong>
              </p>
            ) : null}
          </div>
        </section>
      </div>
    </div>
  );
}